import * as DateFns from "date-fns";
import * as DateFnsTz from "date-fns-tz";
import _ from "lodash";
import * as FSR from "src/generated/hooks/form-service/requests";

type OfferExpirationType = {
  expires_at: string;
  timezone_name: string;
};

type OfferType = {
  expires_at: string | null | undefined;
  timezone_name: string;
};

export type OfferSoftDeleteReturning = {
  id: uuid;
  form_id: uuid;
  school_id: uuid;
  grade_id: uuid | null;
};

/**
 * Returns the number of calendar days remaining until an offer expires.
 * The calculation uses only the date portions (mm-dd-yyyy) in the provided
 * timezone, so time of day does not affect the result.
 */
export const calculateOfferRemainingTime = ({
  expires_at,
  timezone_name,
}: OfferExpirationType) => {
  const today = DateFnsTz.toZonedTime(new Date(), timezone_name);
  const expirationDate = DateFnsTz.toZonedTime(expires_at, timezone_name);

  return Math.max(0, DateFns.differenceInCalendarDays(expirationDate, today));
};

/**
 * Returns whether the offer expiration instant has passed. Current time and
 * `expires_at` are converted with `toZonedTime` in `timezone_name` before
 * comparison (aligned with how expiration is modeled elsewhere in this file).
 */
export const isOfferExpired = ({ expires_at, timezone_name }: OfferType) => {
  if (!expires_at) {
    return false;
  }
  const now = DateFnsTz.toZonedTime(new Date(), timezone_name);
  const expiration = DateFnsTz.toZonedTime(expires_at, timezone_name);
  return DateFns.isAfter(now, expiration);
};

/**
 * Returns true when at least one offer should be treated as still actionable:
 * missing `expires_at`, or not yet past expiration per {@link isOfferExpired}.
 * Offers without an expiry date count as unexpired so legacy data stays usable.
 * An empty array returns false.
 */
export const hasSomeUnexpiredOffers = (offers: OfferType[]) => {
  return offers.some((offer) => {
    if (!offer || !offer.expires_at) {
      return true;
    }
    return !isOfferExpired({
      expires_at: offer.expires_at,
      timezone_name: offer.timezone_name,
    });
  });
};

/**
 * Formats an offer expiration message in the provided timezone.
 * The message indicates whether the offer has already expired.
 */
export const formatOfferExpirationAt = ({
  expires_at,
  timezone_name,
}: OfferExpirationType) => {
  const dateString = DateFnsTz.formatInTimeZone(
    expires_at,
    timezone_name,
    "MM-dd-yyyy, 'at' hh:mm:ss a zzz"
  );
  return isOfferExpired({ expires_at, timezone_name })
    ? `Offer expired on ${dateString}`
    : `Offer will expire on ${dateString}`;
};

export function mapOfferSoftDeleteReturningToGradeIds(
  returning: ReadonlyArray<OfferSoftDeleteReturning>
): uuid[] {
  return _.uniq(
    returning
      .map((offer) => {
        if (!offer.grade_id) {
          console.warn(
            "Skipping rule trigger for soft-deleted offer missing grade_id",
            {
              offerId: offer.id,
              formId: offer.form_id,
              schoolId: offer.school_id,
            }
          );
          return null;
        }
        return offer.grade_id;
      })
      .filter((gradeId): gradeId is uuid => gradeId !== null)
  );
}

export async function triggerRuleExecutionsForSoftDeletedOffers({
  offers,
  scope,
}: {
  offers: readonly OfferSoftDeleteReturning[] | null | undefined;
  scope: "admin" | "me";
}): Promise<void> {
  const gradeIds = mapOfferSoftDeleteReturningToGradeIds(offers ?? []);
  if (gradeIds.length === 0) {
    return;
  }

  const targets = [{ type: "grade" as const, gradeIds: [...gradeIds] }];

  try {
    if (scope === "admin") {
      await FSR.createRuleExecutions({
        body: {
          data: {
            targets,
          },
        },
      });
    } else {
      await FSR.createRuleExecutionsForMe({
        body: {
          data: {
            targets,
          },
        },
      });
    }
  } catch (error) {
    console.warn("Error executing grade-scoped form rules:", error);
  }
}
